import { useEffect, useRef, useState } from 'react';

const sectionPad = 'px-4 sm:px-6 lg:px-10 xl:px-14';

const solutions = [
  {
    key: 'web',
    num: '01',
    title: 'Website Development',
    tagline: 'High-Performance Websites Built for Growth',
    description:
      'We design and develop sophisticated, high-performance websites that strengthen your digital presence, enhance user experience, and convert visitors into customers. From corporate websites to advanced e-commerce platforms, we build scalable digital experiences tailored to your business objectives.',
    features: [
      'Custom Website Design & Development',
      'Responsive & Mobile-First Experiences',
      'Search Engine Optimization (SEO)',
      'Performance, Speed & Security Optimization',
    ],
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
        />
      </svg>
    ),
  },
  {
    key: 'pos',
    num: '02',
    title: 'POS System Development',
    tagline: 'Intelligent Point of Sale Solutions',
    description:
      'We deliver intelligent POS solutions that simplify transactions, optimize inventory control, and provide real-time visibility into your business performance. Our systems help retailers and businesses improve operational efficiency, accuracy, and customer service.',
    features: [
      'Sales & Transaction Management',
      'Inventory & Stock Control',
      'Real-Time Business Reporting',
      'Payment Processing & Receipt Management',
    ],
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
        />
      </svg>
    ),
  },
  {
    key: 'erp',
    num: '03',
    title: 'ERP & Business Management Systems',
    tagline: 'Integrated Solutions for Smarter Business Operations',
    description:
      'We develop comprehensive ERP and business management systems that centralize your operations, streamline workflows, and connect essential business functions within a unified platform. Gain greater control, improve productivity, and make informed decisions through actionable business insights.',
    features: [
      'Financial & Accounting Management',
      'Inventory & Supply Chain Management',
      'Human Resources & Employee Management',
      'Centralized Reporting & Business Analytics',
    ],
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    key: 'custom',
    num: '04',
    title: 'Custom Software Development',
    tagline: 'Tailored Technology. Measurable Business Impact.',
    description:
      'We engineer bespoke software solutions that address your unique operational challenges and business requirements. From specialized business applications to intelligent workflow automation, we transform complex processes into efficient, scalable, and reliable digital solutions.',
    features: [
      'Bespoke Software Engineering',
      'Business Process Automation',
      'API Development & System Integration',
      'Scalable Architecture & Ongoing Maintenance',
    ],
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
];

export default function TechSolutions() {
  const gridRef = useRef(null);
  const [cardsInView, setCardsInView] = useState(false);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setCardsInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="web-development"
      className={`relative scroll-mt-24 border-t border-slate-200/90 bg-gradient-to-b from-white to-slate-50/80 py-10 dark:border-white/10 dark:from-[#050d18] dark:to-brand-950 sm:py-12 lg:py-14 ${sectionPad}`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-32 top-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl dark:bg-primary/15" />
        <div className="absolute -left-24 bottom-24 h-64 w-64 rounded-full bg-secondary/10 blur-3xl dark:bg-secondary/10" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <header className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/90 px-4 py-1.5 shadow-sm dark:border-secondary/25 dark:bg-brand-900/70">
            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-primary to-secondary" aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary dark:text-secondary">
              Web development
            </span>
          </div>
          <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl dark:text-white">
            Software solutions that{' '}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">power growth</span>
          </h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600 dark:text-slate-400 sm:text-lg">
            Websites, POS, ERP, and custom software built to streamline operations and scale with your business.
          </p>
        </header>

        <ul ref={gridRef} className="mt-8 grid gap-4 md:grid-cols-2 lg:mt-10 lg:gap-5">
          {solutions.map((item, index) => (
            <li key={item.key}>
              <article
                style={cardsInView ? { animationDelay: `${index * 0.075}s` } : undefined}
                className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white/90 p-5 shadow-sm ring-1 ring-slate-200/50 transition-all duration-300 will-change-transform motion-reduce:will-change-auto hover:-translate-y-1 hover:border-primary/25 hover:shadow-lg hover:shadow-primary/10 dark:border-white/10 dark:bg-brand-900/50 dark:ring-white/5 dark:hover:border-secondary/30 dark:hover:shadow-secondary/10 sm:p-6 lg:p-7 ${
                  cardsInView
                    ? 'motion-safe:animate-about-card-in motion-reduce:opacity-100 motion-reduce:translate-y-0'
                    : 'opacity-0 translate-y-5 motion-reduce:opacity-100 motion-reduce:translate-y-0'
                }`}
              >
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-primary via-brand-500 to-secondary transition-transform duration-500 ease-out group-hover:scale-x-100"
                  aria-hidden
                />
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-md shadow-primary/35 transition-transform duration-300 ease-out motion-safe:group-hover:scale-110 dark:bg-primary dark:shadow-primary/40">
                    {item.icon}
                  </div>
                  <span
                    className="font-mono text-sm font-bold tabular-nums text-slate-400 sm:text-base dark:text-slate-500"
                    aria-hidden
                  >
                    {item.num}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-bold tracking-tight text-slate-900 sm:text-xl dark:text-white">{item.title}</h3>
                <p className="mt-1 text-sm font-semibold text-primary dark:text-secondary sm:text-base">{item.tagline}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 transition-colors duration-300 group-hover:text-slate-800 dark:text-slate-400 dark:group-hover:text-slate-200 sm:text-base">
                  {item.description}
                </p>

                <div className="mt-5 flex-1 border-t border-slate-200/80 pt-4 dark:border-white/10">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Key features
                  </p>
                  <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                    {item.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <svg
                          className="mt-0.5 h-4 w-4 shrink-0 text-primary dark:text-secondary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href="/#contact"
                  className="mt-5 inline-flex items-center text-sm font-semibold text-primary/90 transition-colors hover:text-primary group-hover:text-primary dark:text-secondary/90 dark:hover:text-secondary dark:group-hover:text-secondary"
                >
                  Get started
                  <svg
                    className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </article>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
